import { useDeliveryApi, useFinanceApi } from 'infrastructure/api';
import { ResourceData, useCrudService } from 'framework/crud';
import { ListResponse } from 'framework/list';
import { BudgetsFinanceType, BudgetsType } from './types';
import { cloneDeep } from 'lodash';
import { SensitiveRecruitingInfo } from 'domain/shared/recruitingFinance';
import { wasteRejectValidation, wasteValidation } from './wasteValidationSchema';
import updateBudgetLayoutValidation from './updateBudgetLayoutValidation';

const useProjectFinanceService = () => {
  const singleResourceUrl = '/projects/:id';
  const {
    search: baseSearch,
    update: initialUpdate,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects',
    useService: useDeliveryApi,
  });

  const { sendPostRequest, sendGetRequest, sendPatchRequest, sendPutRequest } = useDeliveryApi();
  const {
    sendGetRequest: sendGetFinanceRequest,
    sendPostRequest: sendPostFinanceRequest,
    sendPutRequest: sendPutFinanceRequest,
  } = useFinanceApi();

  // budgets

  const createBudget = async (request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/budgets`, request);
    return response.json();
  };

  const budgetsSearch = async (request: ResourceData): Promise<ListResponse<BudgetsType>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/budgets/search`, searchParams);
    return response.json();
  };

  const budgetsFinanceSearch = async (request: ResourceData): Promise<ListResponse<BudgetsFinanceType>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/budgets/sensitive/search`, searchParams);
    return response.json();
  };

  const updateExistingBudget = async (budgetId: string, data: ResourceData): Promise<BudgetsType> => {
    const response = await sendPutRequest(`/budgets/${budgetId}/sow`, data);
    return response.json();
  };

  const updateBudgetInit = async (budgetId: string, data: ResourceData): Promise<BudgetsType> => {
    const response = await sendPutRequest(`/budgets/${budgetId}/complete`, data);
    return response.json();
  };

  const getByBudgetId = async (budgetId: string): Promise<BudgetsType> => {
    const response = await sendGetFinanceRequest(`/project-budgets/${budgetId}`);
    return response.json();
  };

  const updateBudget = async (budgetId: string, data: ResourceData): Promise<BudgetsType> => {
    const response = await sendPatchRequest(`/budgets/${budgetId}`, data);
    return response.json();
  };

  const getBudget = async (budgetId: StringOrNumber): Promise<BudgetsType> => {
    const response = await sendGetRequest(`/budgets/${budgetId}`);
    return response.json();
  };

  const saveSensitiveProjectInfo = async (sensitiveInfo: StringOrNumber): Promise<SensitiveRecruitingInfo> => {
    const res = await sendPostFinanceRequest('/project-finance', { value: sensitiveInfo });

    return res.json();
  };

  const searchSensitiveProjectInfo = async (sensitiveInfo: string[]): Promise<SensitiveRecruitingInfo[]> => {
    const filteredSensitiveInfo = sensitiveInfo.filter(item => item);
    if (!filteredSensitiveInfo.length) {
      return [];
    }

    const res = await sendPostFinanceRequest('/project-finance/search', { ids: filteredSensitiveInfo });
    return res.json();
  };

  // waste

  const wasteSearch = async (request: ResourceData): Promise<any> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostFinanceRequest(`/accounting-cycle/waste/search`, searchParams);
    return response.json();
  };

  const createWaste = async (request: ResourceData): Promise<any> => {
    const response = await sendPostFinanceRequest(`/accounting-cycle/waste`, request);
    return response.json();
  };

  const updateWaste = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPutFinanceRequest(`/accounting-cycle/waste/${id}`, request);
    return response.json();
  };

  const getWasteById = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendGetFinanceRequest(`/accounting-cycle/waste/${id}`, request);
    return response.json();
  };

  const getWasteRejectValidationSchema = () => Promise.resolve(wasteRejectValidation);
  const getWasteValidationSchema = () => Promise.resolve(wasteValidation);
  const getUpdateBudgetLayoutValidationSchema = () => Promise.resolve(updateBudgetLayoutValidation);

  return {
    ...baseCrudRequests,
    getBudget,
    createBudget,
    updateBudget,
    budgetsSearch,
    getByBudgetId,
    updateExistingBudget,
    updateBudgetInit,
    budgetsFinanceSearch,
    saveSensitiveProjectInfo,
    searchSensitiveProjectInfo,
    wasteSearch,
    createWaste,
    updateWaste,
    getWasteById,
    getUpdateBudgetLayoutValidationSchema,
    getWasteValidationSchema,
    getWasteRejectValidationSchema,
  };
};

export default useProjectFinanceService;
